import { Box, Stack, Typography } from '@mui/material';
import { CheckRounded } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { borderRadius, layoutGap } from '../../../theme/theme';

const deliveryCapabilities = [
  'Revit coordination',
  'Structured PCF data',
  'Fabrication handoff',
] as const;

export default function LoginBrandPanel() {
  const theme = useTheme();
  const white = theme.palette.common.white;

  return (
    <Box
      component="section"
      aria-label="PipeEX enterprise workspace"
      sx={{
        display: { xs: 'none', md: 'flex' },
        position: 'relative',
        minWidth: 0,
        minHeight: 0,
        flexDirection: 'column',
        justifyContent: 'space-between',
        overflow: 'hidden',
        p: { md: 4, lg: 5 },
        color: white,
        borderRight: `1px solid ${alpha(theme.palette.divider, 0.55)}`,
        backgroundColor: '#15171c',
        backgroundImage: `radial-gradient(circle at 16% 0%, ${alpha(theme.palette.primary.main, 0.2)} 0%, transparent 38%), linear-gradient(145deg, ${alpha(theme.palette.primary.dark, 0.12)} 0%, transparent 58%)`,
        '@media (max-height: 620px)': {
          p: 3.5,
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          opacity: 0.18,
          pointerEvents: 'none',
          backgroundImage: `linear-gradient(${alpha(white, 0.1)} 1px, transparent 1px), linear-gradient(90deg, ${alpha(white, 0.1)} 1px, transparent 1px)`,
          backgroundSize: '48px 48px',
          maskImage: 'linear-gradient(135deg, black 0%, transparent 72%)',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          width: 260,
          height: 260,
          right: -155,
          bottom: -170,
          border: `48px solid ${alpha(theme.palette.primary.main, 0.08)}`,
          borderRadius: '50%',
          pointerEvents: 'none',
        },
      }}
    >
      <Stack
        direction="row"
        spacing={layoutGap.shell}
        alignItems="center"
        sx={{ position: 'relative', zIndex: 1 }}
      >
        <Box
          component="img"
          src={`${process.env.PUBLIC_URL}/logo.png`}
          alt="PipeEX company logo"
          sx={{
            width: 56,
            height: 56,
            flexShrink: 0,
            borderRadius: `${borderRadius.circle}`,
            boxShadow: `0 0 0 1px ${alpha(white, 0.18)}, 0 12px 28px ${alpha(theme.palette.common.black, 0.32)}`,
          }}
        />
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', letterSpacing: '0.01em', lineHeight: 1.1 }}>
            PipeEX
          </Typography>
          <Typography sx={{ mt: 0.45, color: alpha(white, 0.58), fontSize: '0.7rem', letterSpacing: '0.02em' }}>
            Piping engineering platform
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 430 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ width: 24, height: 2, backgroundColor: 'primary.light' }} />
          <Typography
            variant="overline"
            sx={{ color: alpha(white, 0.58), fontWeight: 800, letterSpacing: '0.14em', fontSize: '0.66rem' }}
          >
            Engineering delivery workspace
          </Typography>
        </Stack>
        <Typography
          component="h2"
          variant="h3"
          sx={{
            mt: 1.5,
            maxWidth: 420,
            fontSize: { md: '2rem', lg: '2.3rem' },
            lineHeight: 1.08,
            fontWeight: 740,
            letterSpacing: '-0.035em',
            '@media (max-height: 620px)': { fontSize: '1.9rem', mt: 1 },
          }}
        >
          Controlled engineering data. Confident fabrication.
        </Typography>
        <Typography
          sx={{
            mt: 1.5,
            maxWidth: 390,
            color: alpha(white, 0.64),
            fontSize: '0.78rem',
            lineHeight: 1.65,
            '@media (max-height: 620px)': { mt: 1 },
          }}
        >
          Keep project context, piping information, and downstream deliverables connected from model coordination to fabrication.
        </Typography>
      </Box>

      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Typography
          variant="overline"
          sx={{ color: alpha(white, 0.38), fontWeight: 800, letterSpacing: '0.12em', fontSize: '0.62rem' }}
        >
          Connected delivery
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: 1.25,
            mt: 1,
          }}
        >
          {deliveryCapabilities.map((capability) => (
            <Stack
              key={capability}
              direction="row"
              spacing={0.65}
              alignItems="flex-start"
              sx={{ pt: 1, borderTop: `1px solid ${alpha(white, 0.14)}` }}
            >
              <CheckRounded sx={{ mt: 0.05, color: 'primary.light', fontSize: 14, flexShrink: 0 }} />
              <Typography sx={{ color: alpha(white, 0.72), fontSize: '0.68rem', lineHeight: 1.4 }}>
                {capability}
              </Typography>
            </Stack>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
